/**
 * "Is somebody actually at this screen?" — for พร้อมรับ switching itself off after N minutes without activity
 * (owner 27 ก.ย. 69, docs/routing.md "ปิดพร้อมรับอัตโนมัติ").
 *
 * Activity = real input in the chat app: pointer down, key press, wheel / scroll, touch, the window getting focus
 * or the tab becoming visible again. Mouse movement alone does not count. Any open tab of the same person counts:
 * tabs tell each other through BroadcastChannel (and localStorage for browsers/tabs without it), so a person who
 * works in one tab is not "idle" in another.
 *
 * The page reports `idle_s` (seconds since the last activity anywhere) in the WebSocket heartbeat; the server keeps
 * staff.last_active_at and turns พร้อมรับ off (api/app/routing/presence.py). Even when the laptop sleeps or every tab
 * is closed, the server decides from the last report — nothing here has to run for the switch to go off.
 */

export const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const
/** Record at most once per this many ms (events fire very often while scrolling / typing). */
export const THROTTLE_MS = 5000
/** Activity after this long a pause is reported at once (someone came back to the desk). */
export const WAKE_AFTER_MS = 60_000
export const STORAGE_KEY = 'cf-chat.active-at'
const CHANNEL_NAME = 'cf-chat-activity'

interface ChannelLike {
  postMessage(msg: unknown): void
  close(): void
  onmessage: ((ev: MessageEvent) => void) | null
}

type Listen = (type: string, fn: (e: Event) => void, opts?: AddEventListenerOptions | boolean) => void

export interface ActivityOptions {
  now?: () => number
  throttleMs?: number
  wakeAfterMs?: number
  /** Where input events are heard (default: window + document). */
  win?: { addEventListener: Listen; removeEventListener: Listen } | null
  doc?: { addEventListener: Listen; removeEventListener: Listen; visibilityState?: string } | null
  /** null = no cross-tab channel. Default: BroadcastChannel when the browser has it. */
  channel?: ChannelLike | null
  storage?: Pick<Storage, 'getItem' | 'setItem'> | null
  /** Called when activity comes after a pause ≥ wakeAfterMs (in this tab or another): report it now. */
  onWake?: () => void
}

function defaultChannel(): ChannelLike | null {
  try {
    return typeof BroadcastChannel === 'function' ? (new BroadcastChannel(CHANNEL_NAME) as unknown as ChannelLike) : null
  } catch {
    return null
  }
}

function defaultStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

export class ActivityTracker {
  /** Last activity in any tab (ms, same clock as `now`). */
  lastActive: number
  private readonly now: () => number
  private readonly throttleMs: number
  private readonly wakeAfterMs: number
  private readonly win: ActivityOptions['win']
  private readonly doc: ActivityOptions['doc']
  private readonly channel: ChannelLike | null
  private readonly storage: Pick<Storage, 'getItem' | 'setItem'> | null
  private readonly onWake?: () => void
  private started = false

  constructor(opts: ActivityOptions = {}) {
    this.now = opts.now ?? (() => Date.now())
    this.throttleMs = opts.throttleMs ?? THROTTLE_MS
    this.wakeAfterMs = opts.wakeAfterMs ?? WAKE_AFTER_MS
    this.win = opts.win === undefined ? (typeof window === 'undefined' ? null : window) : opts.win
    this.doc = opts.doc === undefined ? (typeof document === 'undefined' ? null : document) : opts.doc
    this.channel = opts.channel === undefined ? defaultChannel() : opts.channel
    this.storage = opts.storage === undefined ? defaultStorage() : opts.storage
    this.onWake = opts.onWake
    // opening / reloading the page is something a person does
    this.lastActive = Math.max(this.now(), this.stored())
  }

  private stored(): number {
    try {
      const v = Number(this.storage?.getItem(STORAGE_KEY) ?? 0)
      return Number.isFinite(v) ? v : 0
    } catch {
      return 0
    }
  }

  private readonly onInput = (): void => this.mark()
  private readonly onVisible = (): void => {
    if (!this.doc || this.doc.visibilityState === undefined || this.doc.visibilityState === 'visible') this.mark()
  }
  private readonly onStorage = (e: Event): void => {
    const se = e as StorageEvent
    if (se.key === STORAGE_KEY && se.newValue) this.heard(Number(se.newValue))
  }

  /** Activity in this tab. Throttled; tells the other tabs. */
  mark(): void {
    const t = this.now()
    const prev = this.lastActive
    if (t - prev < this.throttleMs) return
    this.lastActive = t
    try {
      this.channel?.postMessage({ t })
    } catch {
      /* channel closed */
    }
    try {
      this.storage?.setItem(STORAGE_KEY, String(t))
    } catch {
      /* storage blocked (private mode) */
    }
    if (t - prev >= this.wakeAfterMs) this.onWake?.()
  }

  /** Activity another tab told us about. */
  heard(t: number): void {
    if (!Number.isFinite(t) || t <= this.lastActive) return
    const prev = this.lastActive
    this.lastActive = Math.min(t, this.now())
    if (this.lastActive - prev >= this.wakeAfterMs) this.onWake?.()
  }

  /** Whole seconds since the last activity in any tab (what the heartbeat reports). */
  idleSeconds(): number {
    this.lastActive = Math.max(this.lastActive, Math.min(this.stored(), this.now()))
    return Math.max(0, Math.floor((this.now() - this.lastActive) / 1000))
  }

  start(): void {
    if (this.started) return
    this.started = true
    const passive: AddEventListenerOptions = { passive: true, capture: true }
    for (const type of ACTIVITY_EVENTS) this.win?.addEventListener(type, this.onInput, passive)
    this.doc?.addEventListener('scroll', this.onInput, passive) // any scrolling list/thread (capture)
    this.win?.addEventListener('focus', this.onInput)
    this.win?.addEventListener('storage', this.onStorage)
    this.doc?.addEventListener('visibilitychange', this.onVisible)
    if (this.channel) {
      this.channel.onmessage = (ev: MessageEvent) => {
        const t = Number((ev.data as { t?: unknown } | null)?.t)
        this.heard(t)
      }
    }
  }

  stop(): void {
    if (!this.started) return
    this.started = false
    const passive: AddEventListenerOptions = { passive: true, capture: true }
    for (const type of ACTIVITY_EVENTS) this.win?.removeEventListener(type, this.onInput, passive)
    this.doc?.removeEventListener('scroll', this.onInput, passive)
    this.win?.removeEventListener('focus', this.onInput)
    this.win?.removeEventListener('storage', this.onStorage)
    this.doc?.removeEventListener('visibilitychange', this.onVisible)
    if (this.channel) {
      this.channel.onmessage = null
      try {
        this.channel.close()
      } catch {
        /* already closed */
      }
    }
  }
}

export const AUTO_OFF_TITLE = 'ปิดพร้อมรับให้อัตโนมัติ'

/** "เพราะไม่มีการใช้งาน 15 นาที" (minutes → the same wording as the settings page). */
export function autoOffReason(minutes: number | null | undefined): string {
  const m = Math.max(0, Math.round(minutes ?? 0))
  if (!m) return 'เพราะไม่มีการใช้งาน'
  const h = Math.floor(m / 60)
  const r = m % 60
  const dur = h && r ? `${h} ชม. ${r} นาที` : h ? `${h} ชม.` : `${r} นาที`
  return `เพราะไม่มีการใช้งาน ${dur}`
}

/** The whole sentence: "ปิดพร้อมรับให้อัตโนมัติ เพราะไม่มีการใช้งาน 15 นาที". */
export function autoOffText(minutes: number | null | undefined): string {
  return `${AUTO_OFF_TITLE} ${autoOffReason(minutes)}`
}
